import * as yup from 'yup';
import {
    FIELD_CONFIGS,
    VALIDATION_MESSAGES,
    FIELD_SPECIFIC_MESSAGES
} from '../config/formConfig';

/**
 * Get validation error message for a specific field and error type
 * @param {string} fieldName - Name of the field
 * @param {string} errorType - Type of validation error
 * @param {Object} params - Parameters for the error message
 * @returns {string} Formatted error message
 */
export const getValidationMessage = (fieldName, errorType, params = {}) => {
    const fieldConfig = FIELD_CONFIGS[fieldName];
    const fieldLabel = fieldConfig?.label || fieldName;

    // Check for field-specific messages first
    const fieldSpecificMessage =
        FIELD_SPECIFIC_MESSAGES?.[fieldName]?.[errorType];

    // Use field-specific message if available, otherwise use generic message
    const messageTemplate =
        fieldSpecificMessage || VALIDATION_MESSAGES[errorType];
    if (!messageTemplate) return `${fieldLabel} is invalid`;

    // Replace placeholders in message template
    return messageTemplate.replace(/\{(\w+)\}/g, (match, key) => {
        return params[key] || match;
    });
};

/**
 * Generates a Yup validation schema dynamically from field configurations
 * @param {Object} fieldConfigs - Field configuration object
 * @param {string} userType - User type to filter applicable fields
 * @param {Object} selectClub - Selected club object for club-specific validation
 * @returns {yup.ObjectSchema} Yup validation schema
 */
export const generateValidationSchema = (
    fieldConfigs = FIELD_CONFIGS,
    userType = 'PLAYER',
    selectClub = {}
) => {
    const schemaFields = {};

    Object.entries(fieldConfigs).forEach(([fieldName, config]) => {
        // Skip fields not applicable to current user type
        if (config.playerOnly && userType === 'NON_PLAYER') return;
        if (config.nonPlayerOnly && userType === 'PLAYER') return;

        let fieldSchema;

        // Start with base schema based on field type
        switch (config.type) {
            case 'text':
            case 'textarea':
                fieldSchema = yup.string();
                break;
            case 'dropdown':
                fieldSchema = config.allowMultiple
                    ? yup.array().of(yup.string())
                    : yup.string();
                break;
            case 'radio':
                fieldSchema = yup.string();
                break;
            case 'date':
                fieldSchema = yup.date();
                break;
            case 'boolean':
                fieldSchema = yup.boolean();
                break;
            default:
                fieldSchema = yup.string();
        }

        // Apply validation rules if they exist
        if (config.validation) {
            const validation = config.validation;

            // Required validation
            if (config.required || validation.required) {
                const message =
                    validation.requiredMessage ||
                    getValidationMessage(fieldName, 'required');
                fieldSchema = fieldSchema.required(message);
            }

            // String validations
            if (validation.minLength) {
                const message = getValidationMessage(fieldName, 'minLength', {
                    min: validation.minLength
                });
                fieldSchema = fieldSchema.min(validation.minLength, message);
            }

            if (validation.maxLength) {
                const message = getValidationMessage(fieldName, 'maxLength', {
                    max: validation.maxLength
                });
                fieldSchema = fieldSchema.max(validation.maxLength, message);
            }

            // Pattern validation
            if (validation.pattern) {
                const message =
                    validation.message ||
                    getValidationMessage(fieldName, 'invalidPattern');
                fieldSchema = fieldSchema.matches(validation.pattern, message);
            }

            // Options validation (oneOf)
            if (validation.options && validation.options.length > 0) {
                const message = getValidationMessage(fieldName, 'required');
                fieldSchema = fieldSchema.oneOf(validation.options, message);
            }

            // Array validations
            if (config.allowMultiple && validation.maxItems) {
                const message = getValidationMessage(fieldName, 'maxItems', {
                    max: validation.maxItems
                });
                fieldSchema = fieldSchema.max(validation.maxItems, message);
            }

            if (config.allowMultiple && validation.minItems) {
                const message = getValidationMessage(fieldName, 'required');
                fieldSchema = fieldSchema.min(validation.minItems, message);
            }

            // Date validations
            if (config.type === 'date') {
                if (validation.maxDate || validation.noFuture) {
                    const message =
                        validation.maxDateMessage ||
                        getValidationMessage(fieldName, 'maxDate');
                    fieldSchema = fieldSchema.max(
                        validation.maxDate || new Date(),
                        message
                    );
                }

                if (validation.minDate) {
                    const message =
                        validation.minDateMessage ||
                        getValidationMessage(fieldName, 'minDate');
                    fieldSchema = fieldSchema.min(validation.minDate, message);
                }

                // Add type validation for dates
                fieldSchema = fieldSchema.typeError(
                    validation.invalidMessage ||
                        getValidationMessage(fieldName, 'invalidDate')
                );
            }

            // Conditional validation for team name when club is selected
            if (
                fieldName === 'teamName' &&
                validation.requiredWhenClubSelected
            ) {
                const message =
                    validation.requiredWhenClubSelectedMessage ||
                    getValidationMessage(fieldName, 'required');
                fieldSchema = fieldSchema.test(
                    'required-when-club-selected',
                    message,
                    function (value) {
                        const { clubName, contractStatus } = this.parent;
                        // If club is selected, team must also be selected
                        // But if the club type is COMPANY, team is not required
                        if (
                            clubName &&
                            clubName.trim() &&
                            contractStatus !== 'UNSIGNED' &&
                            selectClub?.typeOfOrg !== 'COMPANY'
                        ) {
                            return value && value.trim();
                        }
                        return true; // If no club selected or club is COMPANY, team is optional
                    }
                );
            }

            // Conditional validation for club name when contract status is CONTRACT or NON_CONTRACT
            if (
                fieldName === 'clubName' &&
                validation.requiredWhenContractStatus
            ) {
                const message =
                    validation.requiredWhenContractStatusMessage ||
                    getValidationMessage(fieldName, 'required');
                fieldSchema = fieldSchema.test(
                    'required-when-contract-status',
                    message,
                    function (value) {
                        const { contractStatus } = this.parent;
                        // If contract status is CONTRACT or NON_CONTRACT, club must be selected
                        if (
                            contractStatus === 'CONTRACT' ||
                            contractStatus === 'NON_CONTRACT'
                        ) {
                            return value && value.trim();
                        }
                        return true; // If UNSIGNED or no contract status, club is optional
                    }
                );
            }
        }

        // Make field optional if not required (allow empty strings)
        if (!config.required && !config.validation?.required) {
            fieldSchema = fieldSchema.nullable().optional();
            // Allow empty strings for text fields
            if (config.type === 'text' || config.type === 'textarea') {
                fieldSchema = fieldSchema.transform(value =>
                    value === '' ? null : value
                );
            }
        }

        schemaFields[fieldName] = fieldSchema;
    });

    return yup.object().shape(schemaFields);
};

/**
 * Validate a single field value
 * @param {string} fieldName - Name of the field
 * @param {any} value - Value to validate
 * @param {string} userType - User type
 * @param {Object} selectClub - Selected club object for club-specific validation
 * @returns {Object} Validation result { isValid: boolean, error?: string }
 */
export const validateField = async (
    fieldName,
    value,
    userType = 'PLAYER',
    selectClub = {}
) => {
    const fieldConfig = FIELD_CONFIGS[fieldName];
    if (!fieldConfig) return { isValid: true };

    // Skip validation for fields not applicable to user type
    if (fieldConfig.playerOnly && userType === 'NON_PLAYER')
        return { isValid: true };
    if (fieldConfig.nonPlayerOnly && userType === 'PLAYER')
        return { isValid: true };

    try {
        const schema = generateValidationSchema(
            { [fieldName]: fieldConfig },
            userType,
            selectClub
        );
        await schema.validateAt(fieldName, { [fieldName]: value });
        return { isValid: true };
    } catch (error) {
        return { isValid: false, error: error.message };
    }
};

export default {
    generateValidationSchema,
    getValidationMessage,
    validateField
};
